import type { CartOrderType } from '@order-up/models';
import { removeUndefinedValues } from "../utils/firestore";
import {
  CollectionPaths,
  computeOrderTaxes,
  taxLinesFromCartItems,
} from "@order-up/common";
import {
  Cart,
  CartItem,
  expandComposite,
  ICartData,
  ICartItemData,
  type ComponentSelection,
  type CompositeRefusal,
  type IItem,
  type IOrderDeliveryAddressComponents,
  ITaxRate,
} from "@order-up/models";
import { CartSchema } from "@order-up/schemas";
import { deleteField } from "firebase/firestore";
import {
  DocumentWithId,
  FirestoreRepository,
} from "../interfaces/FirestoreRepository";
import { createLogger } from "../utils/logger";
import { CartStorageService } from "./cartStorage";
import { cartsApi, CartAlreadyConvertedError } from "./cartsApi";
import { resolveLocationDocId, resolveTenantDocId } from "./resolveDocIds";

const logger = createLogger("CartService");

/**
 * Optional cart fields the customer can CLEAR through order config —
 * `scheduledTime: undefined` means "this is an ASAP order now", and dropping a
 * delivery address happens when switching delivery → pickup.
 *
 * A Firestore UPDATE cannot clear a field by omission: `removeUndefinedValues`
 * strips the key from the payload and `updateByQuery` merges, so the old value
 * silently survives. That is exactly how a cart switched back to ASAP kept its
 * stale scheduled slot in Firestore forever — checkout (and the server's
 * SCHEDULED_TIME_PASSED gate) kept seeing a scheduled order whose time had
 * passed. Each of these fields must become an explicit `deleteField()` sentinel
 * when absent from the cart being synced.
 */
const CLEARABLE_CART_FIELDS = [
  "scheduledTime",
  "deliveryAddress",
  "deliveryAddressComponents",
] as const;

/**
 * Which clearable fields are ABSENT from a cleaned Firestore payload (and so
 * need a `deleteField()` sentinel appended). Pure — exported for unit tests.
 */
export function resolveClearedCartFields(
  cleanedPayload: Record<string, unknown>,
): string[] {
  return CLEARABLE_CART_FIELDS.filter((field) => !(field in cleanedPayload));
}

/**
 * Service for cart business logic and operations
 * Handles both local storage and Firestore persistence
 * Follows the same pattern as other services (itemService, employeeService, etc.)
 */
export const cartService = {
  /**
   * Calculate subtotal for cart items
   */
  calculateSubtotal(cart: Cart): number {
    return cart.subtotal;
  },

  /**
   * The tax the checkout will charge for this cart.
   *
   * The ONE engine (`computeOrderTaxes` in `@order-up/common`, the port of the
   * till's), fed the same inputs the API's preview and order creation give it.
   * `orderDiscountTotal` — a loyalty redemption or coupon — comes off the
   * taxable base FIRST, as on the till; this used to tax the full price and
   * leave the discount to the total, showing more tax than the counter charges
   * (order-source unification A4). Raw, unrounded, like the server's figure.
   */
  calculateTax(cart: Cart, taxRates: ITaxRate[], orderDiscountTotal = 0): number {
    if (taxRates.length === 0) return 0;
    return computeOrderTaxes({
      lines: taxLinesFromCartItems(cart.items),
      taxRates,
      orderDiscountTotal,
    }).taxAmount;
  },

  /**
   * Calculate total including tax
   */
  calculateTotal(cart: Cart, taxRates: ITaxRate[] = []): number {
    const subtotal = this.calculateSubtotal(cart);
    const tax = this.calculateTax(cart, taxRates);
    return subtotal + tax;
  },

  /**
   * Add item to cart
   */
  /**
   * Add a COMPOSITE item — one priced parent line plus a real child line per
   * pick.
   *
   * Children are real cart lines on purpose: they become real ORDER lines, and
   * only a real line carries its own `courseId` and `menuGroupId`. That is what
   * lets the snack and the entrée of a Lunch Special fire as separate courses
   * and print at different stations — `Order.courseStates` is keyed by
   * `courseId` and `kitchenTicketEngine` groups printed sections by it, so a
   * modifier could never reach either.
   *
   * REFUSES rather than adding a partial bundle: a guest charged $14.95 for a
   * special missing half its food is worse than a picker that says no. The
   * caller renders `refusals`; nothing is written on refusal.
   *
   * @param compositePrice Resolved by the CALLER through `menuPricingResolver`,
   *   because group pricing and SubgroupReference overrides apply to a
   *   composite like any item and duplicating that walk here is how one dish
   *   ends up with two prices.
   */
  addCompositeToCart(
    cart: Cart,
    item: IItem,
    selections: readonly ComponentSelection[],
    itemsById: ReadonlyMap<number, IItem>,
    compositePrice: number,
    defaults: Pick<ICartItemData, "taxRateIds" | "taxInclusion"> = {},
  ): { cart: Cart } | { refusals: CompositeRefusal[] } {
    const expansion = expandComposite(item, selections, itemsById, compositePrice);
    if (!expansion.ok) return { refusals: expansion.refusals };

    const [parentSpec, ...childSpecs] = expansion.lines;

    // Mint the parent FIRST so its cartItemId can be stamped onto every child.
    // `expandComposite` deliberately mints no ids — id allocation differs per
    // plane, and a resolver that invented them would be non-deterministic.
    const parent = new CartItem({
      menuItemId: parentSpec.menuItemId,
      menuItemGuid: item.guid,
      name: parentSpec.name,
      price: parentSpec.price,
      quantity: 1,
      taxRateIds: defaults.taxRateIds ?? item.taxRateIds ?? [],
      taxInclusion: defaults.taxInclusion ?? false,
      ...(parentSpec.isComposite ? { isComposite: true } : {}),
    });

    let next = cart.addItem(parent);

    for (const child of childSpecs) {
      const childItem = itemsById.get(child.menuItemId);
      next = next.addItem(
        new CartItem({
          menuItemId: child.menuItemId,
          menuItemGuid: childItem?.guid,
          name: child.name,
          price: child.price,
          quantity: 1,
          // Its HOME group, never the composite's — this is what resolves the
          // child's course and prep station server-side.
          ...(child.menuGroupId != null ? { menuGroupId: child.menuGroupId } : {}),
          // The child's OWN rates. Its price is only its upcharge (the
          // composite's price rides on the parent), so this taxes the upcharge
          // and nothing twice. It used to carry none, which sold every paid
          // pick untaxed — House of Pho's Lunch Special upcharges, Oct 2026.
          // The same rates the server's `repriceCart` stamps on the line.
          // (`expandComposite` refuses a pick whose item is unknown, so
          // `childItem` is always found here.)
          taxRateIds: [...(childItem?.taxRateIds ?? [])],
          taxInclusion: defaults.taxInclusion ?? false,
          bundleParentCartItemId: parent.cartItemId,
        }),
      );
    }

    return { cart: CartStorageService.extendCartExpiry(next) };
  },

  addItemToCart(cart: Cart, itemData: ICartItemData): Cart {
    const cartItem = new CartItem(itemData);
    const updatedCart = cart.addItem(cartItem);

    // Extend expiration when adding items
    const extendedCart = CartStorageService.extendCartExpiry(updatedCart);
    return extendedCart;
  },

  /**
   * Update item quantity in cart
   */
  updateItemQuantity(cart: Cart, cartItemId: string, quantity: number): Cart {
    const updatedCart = cart.updateItemQuantity(cartItemId, quantity);
    CartStorageService.saveCart(updatedCart);
    return updatedCart;
  },

  /**
   * Remove item from cart
   */
  removeItemFromCart(cart: Cart, cartItemId: string): Cart {
    const updatedCart = cart.removeItem(cartItemId);
    CartStorageService.saveCart(updatedCart);
    return updatedCart;
  },

  /**
   * Update item notes
   */
  updateItemNotes(cart: Cart, cartItemId: string, notes: string): Cart {
    const updatedCart = cart.updateItemNotes(cartItemId, notes);
    CartStorageService.saveCart(updatedCart);
    return updatedCart;
  },

  /**
   * Replace an existing cart item with updated data (for editing items)
   * This is more efficient than remove + add as it preserves the item's position
   */
  replaceCartItem(
    cart: Cart,
    cartItemId: string,
    updatedItemData: ICartItemData,
  ): Cart {
    logger.debug("Replacing cart item", { cartItemId });

    // Create the new cart item with the same cartItemId to maintain position
    const original = cart.findItem(cartItemId);
    const menuGroupId = updatedItemData.menuGroupId ?? original?.menuGroupId;
    const newCartItem = new CartItem({
      ...updatedItemData,
      cartItemId, // Keep the same ID to replace in place
      addedAt: original?.addedAt || new Date(), // Preserve original addedAt
      // Where the line came from is part of its identity, like `addedAt`: an
      // edit changes options, not the group the guest found the item in. An
      // update that names no group keeps the original's — dropping it sends the
      // line to the kitchen with no prep station (see `replaceItem` in
      // MenuPageClient and `inferCartLineMenuGroupId` server-side).
      ...(menuGroupId != null ? { menuGroupId } : {}),
    });

    const updatedCart = cart.replaceItem(cartItemId, newCartItem);
    CartStorageService.saveCart(updatedCart);

    logger.info("Cart item replaced successfully", { cartItemId });
    return updatedCart;
  },

  /**
   * Clear entire cart
   */
  clearCart(cart: Cart): Cart {
    const clearedCart = cart.clear();
    CartStorageService.saveCart(clearedCart);
    return clearedCart;
  },

  /**
   * Validate cart for checkout
   */
  validateCartForCheckout(cart: Cart): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (cart.isEmpty) {
      errors.push("Cart is empty");
    }

    if (!cart.isValid) {
      errors.push("Some items have invalid modifier selections");
    }

    if (cart.isExpired) {
      errors.push("Cart has expired");
    }

    // Check individual items
    cart.items.forEach((item, index) => {
      if (!item.isValidConfiguration) {
        errors.push(
          `Item ${index + 1} (${item.name}) has invalid modifier configuration`,
        );
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Get cart summary for display
   */
  getCartSummary(cart: Cart, taxRates: ITaxRate[] = []) {
    const subtotal = this.calculateSubtotal(cart);
    const tax = this.calculateTax(cart, taxRates);
    const total = subtotal + tax;

    return {
      itemCount: cart.itemCount,
      uniqueItemCount: cart.uniqueItemCount,
      subtotal,
      tax,
      total,
      isEmpty: cart.isEmpty,
      isValid: cart.isValid,
      isExpired: cart.isExpired,
    };
  },

  /**
   * Check if item can be added to cart (business rules)
   */
  canAddItemToCart(
    cart: Cart,
    itemData: ICartItemData,
  ): {
    canAdd: boolean;
    reason?: string;
  } {
    // Check if restaurant matches
    if (cart.hasItems && cart.restaurantId !== itemData.menuItemId) {
      // Note: This assumes menuItemId relates to restaurant - you may need to adjust this logic
      // based on your actual data structure
    }

    // Add other business rules as needed
    return { canAdd: true };
  },

  /**
   * Find similar items in cart (same menu item, different modifiers)
   */
  findSimilarItems(cart: Cart, menuItemId: number): CartItem[] {
    return cart.findItemsByMenuId(menuItemId);
  },

  /**
   * Get cart item count for a specific menu item
   */
  getMenuItemQuantityInCart(cart: Cart, menuItemId: number): number {
    const items = cart.findItemsByMenuId(menuItemId);
    return items.reduce((total, item) => total + item.quantity, 0);
  },

  /**
   * Get cart item count for a menu item by either numeric ID or GUID
   */
  getMenuItemQuantityByIdentifier(
    cart: Cart,
    menuItemId?: number,
    menuItemGuid?: string,
  ): number {
    const items = cart.findItemsByMenuIdentifier(menuItemId, menuItemGuid);
    return items.reduce((total, item) => total + item.quantity, 0);
  },

  // Firestore operations for authenticated users

  /**
   * Saves cart to Firestore for authenticated users
   * @param cart The cart to save
   * @param tenantId The tenant business ID
   * @param locationNumericId The restaurant business ID
   * @returns Promise resolving to the saved cart with Firestore document ID
   */
  async saveCartToFirestore(
    cart: Cart,
    tenantId: number,
    locationNumericId: number,
    // Pre-resolved doc IDs — passed by upsertCartInFirestore so we don't resolve
    // the tenant/restaurant docs a second time per sync.
    resolved?: { tenantDocId: string; restaurantDocId: string },
  ): Promise<DocumentWithId<Cart>> {
    logger.debug("Saving cart to Firestore", { cartId: cart.cartId });

    try {
      // Resolve document IDs from business IDs (unless already resolved by caller)
      const tenantDocId = resolved?.tenantDocId ?? (await resolveTenantDocId(tenantId));
      if (!tenantDocId) {
        throw new Error(`Tenant not found with tenantId: ${tenantId}`);
      }

      const locationDocId =
        resolved?.restaurantDocId ??
        (await resolveLocationDocId(tenantDocId, locationNumericId));
      if (!locationDocId) {
        throw new Error(
          `Restaurant not found with restaurantId: ${locationNumericId}`,
        );
      }
      // Validate cart data using schema
      const cartData = cart.toJSON();
      const validatedData = CartSchema.parse(cartData);

      // Clean the data for Firestore
      // Convert Date objects to Firestore timestamps (same pattern as items service)
      const cleanedData = removeUndefinedValues({
        ...validatedData,
        createdAt: FirestoreRepository.getServerTimestamp(),
        updatedAt: FirestoreRepository.getServerTimestamp(),
        syncedAt: FirestoreRepository.getServerTimestamp(),
        // expiresAt is kept as Date - Firestore will convert it to Timestamp
      });

      const collectionPath = CollectionPaths.carts(
        tenantDocId,
        locationDocId,
      );
      const cartRepo = new FirestoreRepository<Cart>(collectionPath, Cart);
      const savedCart = await cartRepo.create(cleanedData);

      logger.info("Cart saved to Firestore successfully", {
        cartId: cart.cartId,
      });
      return savedCart;
    } catch (error) {
      logger.error("Failed to save cart to Firestore", {
        cartId: cart.cartId,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Updates cart in Firestore for authenticated users
   * @param cart The cart to update
   * @param tenantId The tenant business ID
   * @param locationNumericId The restaurant business ID
   * @returns Promise resolving when update is complete
   */
  async updateCartInFirestore(
    cart: Cart,
    tenantId: number,
    locationNumericId: number,
    // Pre-resolved doc IDs — passed by upsertCartInFirestore to avoid a second
    // tenant/restaurant doc resolution per sync.
    resolved?: { tenantDocId: string; restaurantDocId: string },
  ): Promise<void> {
    logger.debug("Updating cart in Firestore", { cartId: cart.cartId });

    try {
      // Resolve document IDs from business IDs (unless already resolved by caller)
      const tenantDocId = resolved?.tenantDocId ?? (await resolveTenantDocId(tenantId));
      if (!tenantDocId) {
        throw new Error(`Tenant not found with tenantId: ${tenantId}`);
      }

      const locationDocId =
        resolved?.restaurantDocId ??
        (await resolveLocationDocId(tenantDocId, locationNumericId));
      if (!locationDocId) {
        throw new Error(
          `Restaurant not found with restaurantId: ${locationNumericId}`,
        );
      }
      // Validate cart data using schema
      const cartData = cart.toJSON();
      const validatedData = CartSchema.parse(cartData);

      // Clean the data for Firestore
      // Update timestamps but keep createdAt as-is (same pattern as other services)
      const dataToClean = {
        ...validatedData,
        updatedAt: FirestoreRepository.getServerTimestamp(),
        syncedAt: FirestoreRepository.getServerTimestamp(),
        // createdAt is preserved from original cart (not changed on updates)
      };

      const cleanedData = removeUndefinedValues(dataToClean);

      // Cleared optional fields must be DELETED explicitly — a merge update
      // leaves omitted keys untouched, so without these sentinels a cart
      // switched back to ASAP keeps its stale scheduledTime in Firestore.
      for (const field of resolveClearedCartFields(cleanedData)) {
        cleanedData[field] = deleteField();
      }

      const collectionPath = CollectionPaths.carts(
        tenantDocId,
        locationDocId,
      );
      const cartRepo = new FirestoreRepository<Cart>(collectionPath, Cart);
      // customerId constraint is required for the query to satisfy the carts
      // `allow read` rule (see upsertCartInFirestore for the full rationale).
      await cartRepo.updateByQuery(
        [
          { field: "cartId", operator: "==", value: cart.cartId },
          ...(cart.customerId
            ? [{ field: "customerId", operator: "==" as const, value: cart.customerId }]
            : []),
        ],
        cleanedData,
      );

      logger.info("Cart updated in Firestore successfully", {
        cartId: cart.cartId,
      });
    } catch (error) {
      logger.error("Failed to update cart in Firestore", {
        cartId: cart.cartId,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Upserts cart to Firestore (creates if not exists, updates if exists)
   * This is the recommended method for syncing local cart to Firestore
   * @param cart The cart to sync
   * @param tenantId The tenant business ID
   * @param locationNumericId The restaurant business ID
   * @returns Promise resolving when sync is complete
   */
  async upsertCartInFirestore(
    cart: Cart,
    tenantId: number,
    locationNumericId: number,
  ): Promise<void> {
    logger.debug("Upserting cart to Firestore", { cartId: cart.cartId });

    try {
      // Resolve document IDs from business IDs
      const tenantDocId = await resolveTenantDocId(tenantId);
      if (!tenantDocId) {
        throw new Error(`Tenant not found with tenantId: ${tenantId}`);
      }

      const locationDocId = await resolveLocationDocId(
        tenantDocId,
        locationNumericId,
      );
      if (!locationDocId) {
        throw new Error(
          `Restaurant not found with restaurantId: ${locationNumericId}`,
        );
      }

      // Check if cart exists in Firestore
      const collectionPath = CollectionPaths.carts(
        tenantDocId,
        locationDocId,
      );
      const cartRepo = new FirestoreRepository<Cart>(collectionPath, Cart);
      // Constrain by customerId so the query satisfies the carts `allow read`
      // rule (`resource.data.customerId == request.auth.uid`). Firestore's
      // list-query model rejects a `cartId`-only query for non-staff because it
      // can't prove every result belongs to the caller — that denial is what
      // silently broke cart→Firestore sync for anonymous customers (the cart
      // then never persisted, so checkout couldn't find it). customerId is the
      // current uid (carts are created with it).
      const existingCarts = await cartRepo.find([
        { field: "cartId", operator: "==", value: cart.cartId },
        ...(cart.customerId
          ? [{ field: "customerId", operator: "==" as const, value: cart.customerId }]
          : []),
      ]);

      if (existingCarts.length > 0) {
        // A converted cart already became an order and is TERMINAL. Refuse the
        // write instead of overwriting it — this is the resurrection gate.
        // Checkout no longer deletes the cart (deleting just let the browser's
        // surviving copy recreate the same cartId/guid); it stamps
        // `convertedAt`, and that marker is only load-bearing if a later write
        // cannot clear it. Prod 2026-07-30: a resurrected cart reproduced an
        // identical idempotency signature and replayed a VOIDED payment as a
        // success. A void never reopens a cart — the order happened.
        //
        // The refusal THROWS. It used to `return` — a silent success — so the
        // caller kept handing the converted cart's guid to checkout, which
        // answered 410 "Cart Not Found" on every press (prod 2026-10-03).
        // useCart catches this and moves the items onto a fresh cart.
        if (existingCarts[0].isConverted) {
          logger.warn("Refusing to overwrite a converted cart", {
            cartId: cart.cartId,
            orderGuid: existingCarts[0].orderGuid,
          });
          throw new CartAlreadyConvertedError(existingCarts[0].orderGuid);
        }
        // Cart exists - update it (reuse the doc IDs resolved above)
        logger.debug("Cart exists in Firestore, updating");
        await this.updateCartInFirestore(cart, tenantId, locationNumericId, {
          tenantDocId,
          restaurantDocId: locationDocId,
        });
      } else {
        // Cart doesn't exist - create it (reuse the doc IDs resolved above)
        logger.debug("Cart doesn't exist in Firestore, creating");
        await this.saveCartToFirestore(cart, tenantId, locationNumericId, {
          tenantDocId,
          restaurantDocId: locationDocId,
        });
      }

      logger.info("Cart upserted to Firestore successfully", {
        cartId: cart.cartId,
      });
    } catch (error) {
      logger.error("Failed to upsert cart to Firestore", {
        cartId: cart.cartId,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Cart-handoff owner lookup (cross-app auth). Resolves ONLY the owner UID of
   * a cart by its unguessable GUID, with NO tenant/restaurant context.
   *
   * SOC 2 (C9): this replaces a `collectionGroup('carts')` LIST query (which // scoped-path-ok: prose
   * required a blanket Firestore `allow list` that let any authenticated /
   * anonymous session enumerate every restaurant's carts). The lookup now runs
   * server-side via the authenticated API, which resolves the single cart by
   * its GUID through a collection-group query (admin SDK) and returns ONLY the
   * owner UID — never cart contents. The caller (CustomerAuthHandler) only
   * needs the owner UID to decide whether to keep or replace the session.
   *
   * @param cartGuid The cart GUID (UUID) to look up
   * @returns `{ found, customerId }` — `customerId` is null for an anonymous cart
   */
  async getCartOwnerByGuid(
    cartGuid: string,
  ): Promise<{ found: boolean; customerId: string | null }> {
    logger.debug("Resolving cart owner by GUID (handoff)", { cartGuid });

    try {
      const owner = await cartsApi.getCartOwnerByGuid(cartGuid);
      logger.info("Resolved cart owner by GUID", {
        cartGuid,
        found: owner.found,
      });
      return owner;
    } catch (error) {
      logger.error("Failed to resolve cart owner by GUID", {
        cartGuid,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Load cart by cart GUID (for checkout app).
   *
   * SOC 2 (C9): replaces the prior Firestore `where('guid', ...)` LIST query
   * (which depended on a blanket `allow list`) with the authenticated
   * single-record API, which resolves the cart from its GUID alone.
   *
   * @param cartGuid The cart GUID (UUID) to load
   * @returns Promise resolving to the cart or null if not found / expired
   */
  async loadCartById(cartGuid: string): Promise<Cart | null> {
    logger.debug("Loading cart by GUID via API", { cartGuid });

    try {
      const cart = await cartsApi.getCartByGuid(cartGuid);

      if (!cart) {
        logger.debug("No cart found with GUID", { cartGuid });
        return null;
      }

      // Belt-and-braces: the API answers a converted cart with 410, but a cart
      // reaching here still carrying `convertedAt` must never be treated as a
      // live checkout either.
      if (cart.isConverted) {
        logger.warn("Cart already converted to an order — not a live cart", {
          cartGuid,
          orderGuid: cart.orderGuid,
        });
        return null;
      }

      // Check if cart is expired
      if (cart.isExpired) {
        logger.debug("Cart is expired, returning null", { cartGuid });
        // Don't delete automatically - user might be mid-checkout
        return null;
      }

      logger.info("Successfully loaded cart by GUID", {
        cartGuid,
        cartId: cart.cartId,
        itemCount: cart.items.length,
        totalItems: cart.itemCount,
        restaurantId: cart.restaurantId,
        orderType: cart.orderType,
      });

      return cart;
    } catch (error) {
      // A converted cart is a DEFINITIVE terminal answer, not a failure —
      // rethrowing would make React Query retry a cart that will never come
      // back, and surface an error where the truth is "this order was already
      // placed". Return null so checkout treats it as no live cart.
      if (error instanceof CartAlreadyConvertedError) {
        logger.warn("Cart already placed as an order — no live cart to load", {
          cartGuid,
          orderGuid: error.orderGuid,
        });
        return null;
      }
      // RETHROW — genuine "not found" / "expired" already returned null above.
      // Reaching here is a real API/network failure; returning null would make it
      // masquerade as "cart not found" (and skip React Query's retry). Throwing
      // lets useGetCartById retry, and the checkout page renders cartError.
      logger.error("Failed to load cart by GUID", {
        cartGuid,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Loads cart from Firestore for authenticated users
   * @param customerId The customer ID
   * @param tenantId The tenant business ID
   * @param locationNumericId The restaurant business ID
   * @returns Promise resolving to the cart or null if not found
   */
  async loadCartFromFirestore(
    customerId: string,
    tenantId: number,
    locationNumericId: number,
  ): Promise<DocumentWithId<Cart> | null> {
    logger.debug("Loading cart from Firestore for customer", {
      customerId,
      restaurantId: locationNumericId,
    });

    try {
      // Resolve document IDs from business IDs
      const tenantDocId = await resolveTenantDocId(tenantId);
      if (!tenantDocId) {
        throw new Error(`Tenant not found with tenantId: ${tenantId}`);
      }

      const locationDocId = await resolveLocationDocId(
        tenantDocId,
        locationNumericId,
      );
      if (!locationDocId) {
        throw new Error(
          `Restaurant not found with restaurantId: ${locationNumericId}`,
        );
      }
      const collectionPath = CollectionPaths.carts(
        tenantDocId,
        locationDocId,
      );
      const cartRepo = new FirestoreRepository<Cart>(collectionPath, Cart);
      const carts = await cartRepo.find([
        { field: "customerId", operator: "==", value: customerId },
        { field: "restaurantId", operator: "==", value: locationNumericId },
        { field: "tenantId", operator: "==", value: tenantId },
      ]);

      // A converted cart already became an order — it is history, never the
      // customer's live cart. Returning it here handed every returning guest
      // their LAST order's cart: new items were refused by the resurrection
      // gate in upsertCartInFirestore, and checkout answered 410 ("Cart Not
      // Found") on every press (prod 2026-10-03, House of Pho, iOS + Android).
      const liveCarts = carts.filter((c) => !c.isConverted);

      if (liveCarts.length === 0) {
        logger.debug("No live cart found in Firestore for customer", {
          customerId,
          convertedCarts: carts.length,
        });
        return null;
      }

      // Get the most recent cart if multiple exist
      const sortedCarts = liveCarts.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      const mostRecentCart = sortedCarts[0];

      // Check if cart is expired
      if (mostRecentCart.isExpired) {
        logger.debug("Cart is expired, deleting", {
          cartId: mostRecentCart.cartId,
        });
        await this.deleteCartFromFirestore(
          mostRecentCart.cartId,
          tenantId,
          locationNumericId,
          mostRecentCart.customerId,
        );
        return null;
      }

      logger.info("Successfully loaded cart from Firestore", {
        cartId: mostRecentCart.cartId,
      });
      return mostRecentCart;
    } catch (error) {
      // RETHROW — do NOT swallow to null. A genuine "no cart for this customer"
      // already returns null above (carts.length === 0). Reaching here means a
      // real failure (permission/network/timeout); returning null would make the
      // caller (useCart) treat it as "cart deleted", clear localStorage, and
      // create a fresh empty cart — silently discarding the customer's saved cart
      // and defeating the offline fallback. By throwing, useCart's strategy-1
      // catch sets firestoreQuerySucceeded=false and falls back to localStorage.
      logger.error("Failed to load cart from Firestore", {
        customerId,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Deletes cart from Firestore
   * @param cartId The cart ID (numeric) to delete
   * @param tenantId The tenant business ID
   * @param locationNumericId The restaurant business ID
   * @returns Promise resolving when deletion is complete
   */
  async deleteCartFromFirestore(
    cartId: number,
    tenantId: number,
    locationNumericId: number,
    customerId?: string,
  ): Promise<void> {
    logger.debug("Deleting cart from Firestore", { cartId });

    try {
      // Resolve document IDs from business IDs
      const tenantDocId = await resolveTenantDocId(tenantId);
      if (!tenantDocId) {
        throw new Error(`Tenant not found with tenantId: ${tenantId}`);
      }

      const locationDocId = await resolveLocationDocId(
        tenantDocId,
        locationNumericId,
      );
      if (!locationDocId) {
        throw new Error(
          `Restaurant not found with restaurantId: ${locationNumericId}`,
        );
      }
      const collectionPath = CollectionPaths.carts(
        tenantDocId,
        locationDocId,
      );
      const cartRepo = new FirestoreRepository<Cart>(collectionPath, Cart);
      // customerId constraint lets a customer delete their OWN cart under the
      // carts `allow read`/`allow delete` rules (a cartId-only query is denied
      // for non-staff — see upsertCartInFirestore). Callers in the customer flow
      // pass the owning uid; staff/cleanup paths omit it and rely on staff list.
      await cartRepo.deleteByQuery([
        { field: "cartId", operator: "==", value: cartId },
        ...(customerId
          ? [{ field: "customerId", operator: "==" as const, value: customerId }]
          : []),
      ]);

      logger.info("Cart deleted from Firestore successfully", { cartId });
    } catch (error) {
      logger.error("Failed to delete cart from Firestore", {
        cartId,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Merges guest cart with authenticated user's existing cart
   * @param guestCart The guest cart to merge
   * @param customerId The customer ID to merge with
   * @returns Promise resolving to the merged cart
   */
  async mergeGuestCartWithFirestore(
    guestCart: Cart,
    customerId: string,
  ): Promise<Cart> {
    logger.debug("Merging guest cart with customer cart", { customerId });

    try {
      // Load existing customer cart
      const existingCart = await this.loadCartFromFirestore(
        customerId,
        guestCart.tenantId,
        guestCart.restaurantId,
      );

      let mergedCart: Cart;

      if (existingCart) {
        logger.debug("Found existing cart, merging items");
        // Merge items from guest cart into existing cart
        mergedCart = existingCart;
        for (const guestItem of guestCart.items) {
          mergedCart = mergedCart.addItem(guestItem);
        }
      } else {
        logger.debug(
          "No existing cart, converting guest cart to customer cart",
        );
        // Convert guest cart to customer cart
        mergedCart = new Cart({
          cartId: guestCart.cartId,
          guid: guestCart.guid,
          customerId,
          restaurantId: guestCart.restaurantId,
          tenantId: guestCart.tenantId,
          items: guestCart.items,
          orderType: guestCart.orderType,
          createdAt: guestCart.createdAt,
          updatedAt: new Date(),
          expiresAt: guestCart.expiresAt,
          syncedAt: guestCart.syncedAt,
          version: guestCart.version,
          isGuest: false,
        });
      }

      // Save merged cart to Firestore
      if (existingCart) {
        await this.updateCartInFirestore(
          mergedCart,
          guestCart.tenantId,
          guestCart.restaurantId,
        );
      } else {
        await this.saveCartToFirestore(
          mergedCart,
          guestCart.tenantId,
          guestCart.restaurantId,
        );
      }

      // Clear guest cart from local storage
      CartStorageService.clearCart();

      logger.info("Guest cart merged with customer cart successfully");
      return mergedCart;
    } catch (error) {
      logger.error("Failed to merge guest cart", {
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Cleans up expired carts from Firestore for a specific restaurant
   * @param tenantId The tenant business ID
   * @param locationNumericId The restaurant business ID
   * @param olderThanDays Number of days after which to consider carts as expired
   * @returns Promise resolving to the number of carts cleaned
   */
  async cleanupExpiredCarts(
    tenantId: number,
    locationNumericId: number,
    olderThanDays: number = 7,
  ): Promise<number> {
    logger.debug("Cleaning up expired carts", { restaurantId: locationNumericId, olderThanDays });

    try {
      // Resolve document IDs from business IDs
      const tenantDocId = await resolveTenantDocId(tenantId);
      if (!tenantDocId) {
        throw new Error(`Tenant not found with tenantId: ${tenantId}`);
      }

      const locationDocId = await resolveLocationDocId(
        tenantDocId,
        locationNumericId,
      );
      if (!locationDocId) {
        throw new Error(
          `Restaurant not found with restaurantId: ${locationNumericId}`,
        );
      }
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

      const collectionPath = CollectionPaths.carts(
        tenantDocId,
        locationDocId,
      );
      const cartRepo = new FirestoreRepository<Cart>(collectionPath, Cart);
      const expiredCarts = await cartRepo.find([
        { field: "updatedAt", operator: "<", value: cutoffDate },
      ]);

      if (expiredCarts.length === 0) {
        logger.debug("No expired carts found");
        return 0;
      }

      // Delete expired carts in batches
      const batchSize = 10;
      let deletedCount = 0;

      for (let i = 0; i < expiredCarts.length; i += batchSize) {
        const batch = expiredCarts.slice(i, i + batchSize);
        const deletePromises = batch.map((cart) =>
          this.deleteCartFromFirestore(cart.cartId, tenantId, locationNumericId, cart.customerId),
        );
        await Promise.all(deletePromises);
        deletedCount += batch.length;
      }

      logger.info("Expired carts cleaned up successfully", { deletedCount });
      return deletedCount;
    } catch (error) {
      logger.error("Failed to cleanup expired carts", {
        error: error instanceof Error ? error.message : String(error),
      });
      return 0;
    }
  },

  /**
   * Gets cart analytics for a restaurant
   * @param tenantId The tenant business ID
   * @param locationNumericId The restaurant business ID
   * @param startDate Start date for analytics
   * @param endDate End date for analytics
   * @returns Promise resolving to cart analytics
   */
  async getCartAnalytics(
    tenantId: number,
    locationNumericId: number,
    startDate: Date,
    endDate: Date,
  ) {
    logger.debug("Getting cart analytics for restaurant", { restaurantId: locationNumericId });

    try {
      // Resolve document IDs from business IDs
      const tenantDocId = await resolveTenantDocId(tenantId);
      if (!tenantDocId) {
        throw new Error(`Tenant not found with tenantId: ${tenantId}`);
      }

      const locationDocId = await resolveLocationDocId(
        tenantDocId,
        locationNumericId,
      );
      if (!locationDocId) {
        throw new Error(
          `Restaurant not found with restaurantId: ${locationNumericId}`,
        );
      }
      const collectionPath = CollectionPaths.carts(
        tenantDocId,
        locationDocId,
      );
      const cartRepo = new FirestoreRepository<Cart>(collectionPath, Cart);
      const carts = await cartRepo.find([
        { field: "restaurantId", operator: "==", value: locationNumericId },
        { field: "tenantId", operator: "==", value: tenantId },
        { field: "createdAt", operator: ">=", value: startDate },
        { field: "createdAt", operator: "<=", value: endDate },
      ]);

      const analytics = {
        totalCarts: carts.length,
        totalItems: 0,
        totalValue: 0,
        averageCartValue: 0,
        averageItemsPerCart: 0,
        guestCarts: 0,
        authenticatedCarts: 0,
        pickupCarts: 0,
        deliveryCarts: 0,
      };

      carts.forEach((cart) => {
        analytics.totalItems += cart.itemCount;
        analytics.totalValue += cart.subtotal;
        if (cart.isAnonymous) {
          analytics.guestCarts++;
        } else {
          analytics.authenticatedCarts++;
        }
        if (cart.orderType === "pickup") {
          analytics.pickupCarts++;
        } else {
          analytics.deliveryCarts++;
        }
      });

      analytics.averageCartValue =
        analytics.totalCarts > 0
          ? analytics.totalValue / analytics.totalCarts
          : 0;
      analytics.averageItemsPerCart =
        analytics.totalCarts > 0
          ? analytics.totalItems / analytics.totalCarts
          : 0;

      logger.info("Cart analytics calculated successfully");
      return analytics;
    } catch (error) {
      logger.error("Failed to get cart analytics", {
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },

  /**
   * Update order configuration (orderType, scheduledTime, deliveryAddress) in cart
   * Note: Passing undefined for scheduledTime explicitly clears it (for ASAP orders)
   */
  updateOrderConfig(
    cart: Cart,
    orderConfig: {
      // The last of nine declarations that narrowed the cart's fulfilment to
      // two values. Each one was a place a dine-in order would have been
      // silently mislabelled; this is the bottom of the client stack.
      orderType?: CartOrderType;
      scheduledTime?: Date;
      deliveryAddress?: string;
      deliveryAddressComponents?: IOrderDeliveryAddressComponents;
    },
  ): Cart {
    logger.debug("Updating order config for cart", {
      cartId: cart.cartId,
      input: {
        orderType: orderConfig.orderType,
        scheduledTime: orderConfig.scheduledTime,
        deliveryAddress: orderConfig.deliveryAddress,
      },
    });

    // Create updated cart with new order config
    // Important: Use 'in' operator to distinguish between undefined (clear value) and not provided (keep current)
    const cartJson = cart.toJSON();
    const now = new Date();
    const updatedCartData: ICartData = {
      cartId: cartJson.cartId,
      guid: cartJson.guid,
      customerId: cartJson.customerId,
      restaurantId: cartJson.restaurantId,
      tenantId: cartJson.tenantId,
      items: cart.items, // Already CartItem instances
      orderType:
        "orderType" in orderConfig ? orderConfig.orderType! : cart.orderType,
      // If scheduledTime is in orderConfig (even if undefined), use it; otherwise keep current
      // undefined = ASAP order, Date = scheduled order
      scheduledTime:
        "scheduledTime" in orderConfig
          ? orderConfig.scheduledTime
          : cart.scheduledTime,
      deliveryAddress:
        "deliveryAddress" in orderConfig
          ? orderConfig.deliveryAddress
          : cart.deliveryAddress,
      deliveryAddressComponents:
        "deliveryAddressComponents" in orderConfig
          ? orderConfig.deliveryAddressComponents
          : cart.deliveryAddressComponents,
      configuredAt: now, // Mark when user completed order configuration
      createdAt: cart.createdAt,
      updatedAt: now,
      expiresAt: cart.expiresAt,
      syncedAt: cart.syncedAt,
      version: cart.version,
      isGuest: cart.isGuest,
      // Attribution is not order config and never cleared by the guest — it
      // must survive this field-by-field rebuild or the order loses its QR.
      guestCodeGuid: cart.guestCodeGuid,
    };

    const updatedCart = new Cart(updatedCartData);

    logger.info("Order config updated for cart", { cartId: cart.cartId });
    return updatedCart;
  },

  /**
   * Stamp the guest code (printed QR) that brought this guest in. The value
   * rides the cart document to the checkout route, which re-validates it
   * against the cart's own restaurant before anything reaches the order — so
   * this is a claim, never the attribution itself.
   *
   * Last touch wins: a later scan of a different code replaces it. Nothing in
   * the guest's flow clears it (it is deliberately NOT a clearable field).
   */
  setGuestCodeGuid(cart: Cart, guestCodeGuid: string): Cart {
    return new Cart({
      ...cart.toJSON(),
      items: cart.items,
      updatedAt: new Date(),
      guestCodeGuid,
    });
  },
};
